import { alpha } from '@mui/material/styles';

export const colorsPrimitives = {
  base: {
    black: '#000000',
    white: '#F3F3F3',
    pureWhite: '#FFFFFF',
  },
  neutral: {
    50: '#D4D4D4',
    100: '#B1B1B1',
    200: '#8E8E8E',
    300: '#6B6B6B',
    400: '#494949',
    500: '#262626',
    600: '#232222',
    700: '#171717',
    800: '#0F0F0F',
    900: '#0A0A0A',
  },
  emerald: {
    50: '#D9F0E7',
    100: '#BBE3D3',
    200: '#9CD7C0',
    250: '#9AD3BC',
    300: '#7ECBAC',
    400: '#5FBE99',
    500: '#41B285',
    600: '#2F8160',
    700: '#26684E',
    800: '#1D503B',
    900: '#143729',
    950: '#141F1B',
  },
  green: {
    50: '#CDF6E6',
    100: '#A5EED1',
    200: '#7EE7BD',
    300: '#56E0A9',
    400: '#2ED894',
    500: '#06D180',
    600: '#05B46E',
    700: '#04975D',
    800: '#035D39',
    900: '#024028',
  },
  blue: {
    50: '#F1F7FF',
    100: '#E4F0FF',
    200: '#D6E9FF',
    250: '#D0E3F9',
    300: '#C9E2FF',
    400: '#BBDAFF',
    500: '#AED3FF',
    600: '#96B6DC',
    700: '#7E99B9',
    800: '#667B95',
    900: '#4E5E72',
    950: '#2F363F',
    960: '#252A2F',
  },
  darkBlue: {
    50: '#DFE4EB',
    100: '#C5CFDB',
    200: '#ACBACB',
    300: '#92A5BB',
    400: '#798FAB',
    500: '#5F7A9B',
    600: '#526986',
    700: '#455870',
    800: '#38475B',
    900: '#2A3745',
  },
  yellow: {
    50: '#FDECCE',
    100: '#FBDCA7',
    200: '#FACD80',
    300: '#F8BD59',
    400: '#F7AE32',
    500: '#F59E0B',
    600: '#D3880A',
    700: '#B17208',
    800: '#8F5C06',
    900: '#6D4705',
    950: '#33280A',
  },
  orange: {
    50: '#FEE3D0',
    100: '#FDCDAB',
    200: '#FCB686',
    300: '#FBA061',
    400: '#FA893B',
    500: '#F97316',
    600: '#D76313',
    700: '#B45310',
    800: '#92430D',
    900: '#6F330A',
    950: '#3A1C08',
  },
  red: {
    50: '#FCDADA',
    100: '#F9BCBC',
    200: '#F79E9E',
    300: '#F48080',
    400: '#F26262',
    500: '#EF4444',
    600: '#CE3B3B',
    700: '#AD3131',
    800: '#8C2828',
    900: '#6B1E1E',
    950: '#3C1A1A',
  },
  purple: {
    50: '#EEDDFD',
    100: '#E0C2FC',
    200: '#D2A7FB',
    300: '#C48BFA',
    400: '#B670F8',
    500: '#A855F7',
    600: '#9149D5',
    700: '#7A3EB3',
    800: '#623291',
    900: '#4B266E',
    950: '#2C1944',
  },
};

export const colors = {
  background: {
    canvas: colorsPrimitives.neutral[800],
    default: colorsPrimitives.neutral[900],
    raised: colorsPrimitives.neutral[700],
    overlay: colorsPrimitives.neutral[600],
    inverse: colorsPrimitives.base.white,
  },
  text: {
    primary: colorsPrimitives.base.white,
    secondary: colorsPrimitives.neutral[200],
    tertiary: colorsPrimitives.neutral[300],
    disabled: colorsPrimitives.neutral[400],
    inverse: colorsPrimitives.neutral[900],
    brand: colorsPrimitives.emerald[500],
    warning: colorsPrimitives.yellow[300],
    error: colorsPrimitives.red[500],
    info: colorsPrimitives.blue[500],
    accent: colorsPrimitives.purple[500],
  },
  icon: {
    primary: colorsPrimitives.base.white,
    secondary: colorsPrimitives.neutral[200],
    tertiary: colorsPrimitives.neutral[300],
    disabled: colorsPrimitives.neutral[400],
    inverse: colorsPrimitives.neutral[900],
    brand: colorsPrimitives.emerald[500],
    warning: colorsPrimitives.yellow[300],
    error: colorsPrimitives.red[500],
    info: colorsPrimitives.blue[500],
    accent: colorsPrimitives.purple[500],
  },
  border: {
    subtle: colorsPrimitives.neutral[700],
    default: colorsPrimitives.neutral[500],
    strong: colorsPrimitives.neutral[300],
    brand: colorsPrimitives.emerald[500],
    focus: colorsPrimitives.neutral[300],
    inverse: colorsPrimitives.base.white,
  },
  brand: {
    solid: colorsPrimitives.emerald[500],
    hover: colorsPrimitives.emerald[600],
    active: colorsPrimitives.emerald[700],
    subtle: colorsPrimitives.emerald[950],
    subtleHover: colorsPrimitives.emerald[900],
    subtleActive: colorsPrimitives.emerald[800],
    muted: colorsPrimitives.emerald[800],
    text: colorsPrimitives.emerald[500],
    textSubtle: colorsPrimitives.emerald[250],
    onBrand: colorsPrimitives.base.white,
  },
  info: {
    solid: colorsPrimitives.blue[500],
    hover: colorsPrimitives.blue[600],
    active: colorsPrimitives.blue[700],
    subtle: colorsPrimitives.blue[950],
    subtleAlt: colorsPrimitives.blue[960],
    subtleHover: colorsPrimitives.blue[900],
    subtleActive: colorsPrimitives.blue[800],
    text: colorsPrimitives.blue[500],
    textSubtle: colorsPrimitives.blue[250],
    border: colorsPrimitives.blue[800],
    onInfo: colorsPrimitives.neutral[900],
  },
  success: {
    solid: colorsPrimitives.green[500],
    hover: colorsPrimitives.green[300],
    subtle: colorsPrimitives.green[900],
    subtleHover: colorsPrimitives.green[800],
    text: colorsPrimitives.green[500],
    border: colorsPrimitives.green[700],
    onSuccess: colorsPrimitives.green[900],
  },
  warning: {
    solid: colorsPrimitives.yellow[500],
    hover: colorsPrimitives.yellow[600],
    active: colorsPrimitives.yellow[700],
    subtle: colorsPrimitives.yellow[950],
    subtleHover: colorsPrimitives.yellow[900],
    subtleActive: colorsPrimitives.yellow[800],
    text: colorsPrimitives.yellow[300],
    border: colorsPrimitives.yellow[700],
    onWarning: colorsPrimitives.neutral[900],
  },
  error: {
    solid: colorsPrimitives.red[500],
    hover: colorsPrimitives.red[400],
    active: colorsPrimitives.red[600],
    subtle: colorsPrimitives.red[950],
    subtleHover: colorsPrimitives.red[900],
    subtleActive: colorsPrimitives.red[800],
    text: colorsPrimitives.red[500],
    border: colorsPrimitives.red[700],
    onError: colorsPrimitives.base.white,
  },
  orange: {
    solid: colorsPrimitives.orange[500],
    hover: colorsPrimitives.orange[400],
    subtle: colorsPrimitives.orange[950],
    subtleHover: colorsPrimitives.orange[900],
    text: colorsPrimitives.orange[300],
    border: colorsPrimitives.orange[700],
    onOrange: colorsPrimitives.neutral[900],
  },
  accent: {
    solid: colorsPrimitives.purple[500],
    hover: colorsPrimitives.purple[400],
    subtle: colorsPrimitives.purple[950],
    subtleHover: colorsPrimitives.purple[900],
    text: colorsPrimitives.purple[300],
    border: colorsPrimitives.purple[700],
    onAccent: colorsPrimitives.base.white,
  },
  neutral: {
    solid: colorsPrimitives.neutral[700],
    hover: colorsPrimitives.neutral[600],
    active: colorsPrimitives.neutral[800],
    subtle: colorsPrimitives.neutral[900],
    subtleHover: colorsPrimitives.neutral[800],
    subtleActive: colorsPrimitives.neutral[700],
    onNeutral: colorsPrimitives.base.white,
  },
  white: {
    0: alpha(colorsPrimitives.base.white, 0),
    4: alpha(colorsPrimitives.base.white, 0.04),
    8: alpha(colorsPrimitives.base.white, 0.08),
    12: alpha(colorsPrimitives.base.white, 0.12),
    16: alpha(colorsPrimitives.base.white, 0.16),
    20: alpha(colorsPrimitives.base.white, 0.2),
    30: alpha(colorsPrimitives.base.white, 0.3),
    40: alpha(colorsPrimitives.base.white, 0.4),
    50: alpha(colorsPrimitives.base.white, 0.5),
    60: alpha(colorsPrimitives.base.white, 0.6),
    70: alpha(colorsPrimitives.base.white, 0.7),
    80: alpha(colorsPrimitives.base.white, 0.8),
    90: alpha(colorsPrimitives.base.white, 0.9),
    100: colorsPrimitives.base.white,
  },
  black: {
    0: alpha(colorsPrimitives.base.black, 0),
    4: alpha(colorsPrimitives.base.black, 0.04),
    5: alpha(colorsPrimitives.base.black, 0.05),
    8: alpha(colorsPrimitives.base.black, 0.08),
    10: alpha(colorsPrimitives.base.black, 0.1),
    12: alpha(colorsPrimitives.base.black, 0.12),
    16: alpha(colorsPrimitives.base.black, 0.16),
    20: alpha(colorsPrimitives.base.black, 0.2),
    25: alpha(colorsPrimitives.base.black, 0.25),
    30: alpha(colorsPrimitives.base.black, 0.3),
    40: alpha(colorsPrimitives.base.black, 0.4),
    50: alpha(colorsPrimitives.base.black, 0.5),
    60: alpha(colorsPrimitives.base.black, 0.6),
    70: alpha(colorsPrimitives.base.black, 0.7),
    80: alpha(colorsPrimitives.base.black, 0.8),
    90: alpha(colorsPrimitives.base.black, 0.9),
    100: colorsPrimitives.base.black,
  },
  brandAlpha: {
    0: alpha(colorsPrimitives.emerald[500], 0),
    4: alpha(colorsPrimitives.emerald[500], 0.04),
    8: alpha(colorsPrimitives.emerald[500], 0.08),
    12: alpha(colorsPrimitives.emerald[500], 0.12),
    16: alpha(colorsPrimitives.emerald[500], 0.16),
    20: alpha(colorsPrimitives.emerald[500], 0.2),
    25: alpha(colorsPrimitives.emerald[500], 0.25),
    30: alpha(colorsPrimitives.emerald[500], 0.3),
    40: alpha(colorsPrimitives.emerald[500], 0.4),
    50: alpha(colorsPrimitives.emerald[500], 0.5),
    60: alpha(colorsPrimitives.emerald[500], 0.6),
    70: alpha(colorsPrimitives.emerald[500], 0.7),
    80: alpha(colorsPrimitives.emerald[500], 0.8),
    90: alpha(colorsPrimitives.emerald[500], 0.9),
    100: colorsPrimitives.emerald[500],
  },
  social: {
    linkedin: '#0177B5',
  },
  state: {
    hover: alpha(colorsPrimitives.base.white, 0.08),
    hoverStrong: alpha(colorsPrimitives.base.white, 0.12),
    hoverRaised: colorsPrimitives.neutral[600],
    hoverBorder: colorsPrimitives.neutral[300],
    hoverText: colorsPrimitives.base.white,
  },
};
